// src/components/Cart.jsx
import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { clearCart } from '../store/slices/cartSlice';
import { updateUserProfile, setDeliveryMethod } from '../store/slices/authslice';
import { useNavigate } from 'react-router-dom';
import { Truck, Store, MapPin, Phone, User } from 'lucide-react';
import CartItem from './CartItem';
import { toast } from 'react-toastify';
import AuthDropdown from '../components/AuthDropdown';
import { getFirestore, collection, getDocs } from 'firebase/firestore';

const Cart = () => {
  const { items, totalPrice } = useSelector((state) => state.cart);
  const { user, currentOrderDeliveryMethod } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // State for address toggle and input
  const [useDefaultAddress, setUseDefaultAddress] = useState(true);
  const [differentAddress, setDifferentAddress] = useState('');
  // NEW: State to store locations (with fees) from Firestore
  const [locationsData, setLocationsData] = useState([]);
  // NEW: State for the selected location when using a different address
  const [selectedLocation, setSelectedLocation] = useState('');

  // State for contact phone toggle and input
  const [useDefaultPhone, setUseDefaultPhone] = useState(true);
  const [differentPhone, setDifferentPhone] = useState('');

  // State for payment details (payer toggle and name)
  const [isDifferentPayer, setIsDifferentPayer] = useState(false);
  const [payerName, setPayerName] = useState('');

  // Auth modal state
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Fetch locations from Firestore on component mount
  useEffect(() => {
    const fetchLocations = async () => {
      const db = getFirestore();
      const locationsCollection = collection(db, 'locations');
      try {
        const snapshot = await getDocs(locationsCollection);
        const locationsList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setLocationsData(locationsList);
      } catch (error) {
        console.error("Error fetching locations: ", error);
        toast.error("Failed to fetch locations.");
      }
    };

    fetchLocations();
  }, []);

  // Compute delivery fee based on delivery method and address type
  let deliveryFee = 0;
  if (currentOrderDeliveryMethod === 'delivery') {
    if (useDefaultAddress) {
      // When using default address, use the user's stored location
      const defaultLoc = user?.location && locationsData.find(loc => loc.name === user.location);
      if (defaultLoc) deliveryFee = defaultLoc.fee;
    } else {
      // When using a different address, use the selected location from dropdown
      const diffLoc = selectedLocation && locationsData.find(loc => loc.name === selectedLocation);
      if (diffLoc) deliveryFee = diffLoc.fee;
    }
  }
  const finalTotal = totalPrice + deliveryFee;

  // Handler for toggling between pick-up and delivery
  const handleDeliveryChange = (e) => {
    const method = e.target.value;
    dispatch(setDeliveryMethod(method));
    // Reset address-related state when switching to pick-up
    if (method === 'pick-up') {
      setUseDefaultAddress(true);
      setDifferentAddress('');
    }
  };

  // Handler for different address text input
  const handleDifferentAddressChange = (e) => {
    setDifferentAddress(e.target.value);
  };

  // NEW: Handler for location dropdown when using a different address
  const handleLocationChange = (e) => {
    setSelectedLocation(e.target.value);
  };

  // Handler for proceeding to payment
  const handlePayment = () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    if (!user.username || !user.phone) {
      toast.error('Please complete your profile details before proceeding.');
      return;
    }

    if (currentOrderDeliveryMethod === 'delivery') {
      if (useDefaultAddress && !user.address) {
        toast.error('Please add a default address to your profile or choose a different address.');
        return;
      }
      if (!useDefaultAddress && !differentAddress) {
        toast.error('Please enter the delivery address.');
        return;
      }
      
      // Update user profile with different address if needed
      if (!useDefaultAddress) {
        dispatch(updateUserProfile({ address: differentAddress }));
      }
    }

    // Store order details including the delivery fee before navigation
    const orderDetails = {
      contactPhone: useDefaultPhone ? user.phone : differentPhone,
      differentPayer: isDifferentPayer,
      payerName: isDifferentPayer ? payerName : '',
      deliveryAddress:
        currentOrderDeliveryMethod === 'delivery'
          ? (useDefaultAddress ? user.address : differentAddress)
          : null,
      deliveryFee,
      finalTotal,
    };
    localStorage.setItem('orderDetails', JSON.stringify(orderDetails));
    
    navigate('/payment');
  };

  if (items.length === 0) {
    return (
      <div className="w-full min-h-screen px-4 md:px-8 lg:px-16 py-12 bg-black text-white flex flex-col justify-center items-center">
        <h2 className="text-3xl font-bold text-center mb-6 text-red-500">Your Cart</h2>
        <p className="text-center text-xl">Your cart is empty!</p>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen px-4 md:px-8 lg:px-16 py-12 bg-black text-white">
      <h2 className="text-3xl font-bold text-center mb-6 text-red-500">Your Cart</h2>

      <div className="space-y-4">
        {items.map((item) => (
          <CartItem key={item.id} item={item} />
        ))}
        
        <div className="mt-6 flex justify-between items-center bg-gray-800 p-4 rounded-lg">
          <button
            onClick={() => dispatch(clearCart())}
            className="px-6 py-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors"
          >
            Clear Cart
          </button>

          <div className="text-white">
            <p className="text-lg">
              Subtotal: ₦{totalPrice.toLocaleString()}
            </p>
            {currentOrderDeliveryMethod === 'delivery' && (
              <p className="text-lg">
                Delivery Fee: ₦{deliveryFee.toLocaleString()}
              </p>
            )}
            <p className="text-lg font-semibold">
              Final Total: ₦{finalTotal.toLocaleString()}
            </p>
            <p className="text-sm text-gray-400">
              {currentOrderDeliveryMethod === 'delivery'
                ? 'Delivery fee added'
                : 'No delivery fee for pick-up'}
            </p>
          </div>
        </div>

        {user && (
          <div className="mt-6 bg-gray-800 border border-gray-700 rounded-lg shadow-sm">
            <div className="p-6">
              {/* Delivery Method Section */}
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-red-500">
                <Truck className="w-5 h-5 text-red-500" />
                Choose Delivery Method
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <label
                  className={`flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    currentOrderDeliveryMethod === 'delivery'
                      ? 'border-red-600 bg-gray-900'
                      : 'border-gray-700 hover:border-red-400'
                  }`}
                >
                  <input
                    type="radio"
                    name="delivery-method"
                    value="delivery"
                    checked={currentOrderDeliveryMethod === 'delivery'}
                    onChange={handleDeliveryChange}
                    className="hidden"
                  />
                  <Truck className="w-6 h-6 text-red-500" />
                  <span>Home Delivery</span>
                </label>

                <label
                  className={`flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    currentOrderDeliveryMethod === 'pick-up'
                      ? 'border-red-600 bg-gray-900'
                      : 'border-gray-700 hover:border-red-400'
                  }`}
                >
                  <input
                    type="radio"
                    name="delivery-method"
                    value="pick-up"
                    checked={currentOrderDeliveryMethod === 'pick-up'}
                    onChange={handleDeliveryChange}
                    className="hidden"
                  />
                  <Store className="w-6 h-6 text-red-500" />
                  <span>Store Pickup</span>
                </label>
              </div>

              {/* Contact Phone Section */}
              <div className="mb-6 border-b border-gray-700 pb-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-red-500">
                  <Phone className="w-5 h-5 text-red-500" />
                  Contact Phone
                </h3>
                {/* Toggle buttons for contact phone */}
                <div className="flex gap-4 mb-4">
                  <button
                    onClick={() => setUseDefaultPhone(true)}
                    className={`w-full p-2 rounded-md border transition-colors ${
                      useDefaultPhone
                        ? 'bg-red-500 text-white border-red-500'
                        : 'bg-gray-800 text-white border-gray-600 hover:border-red-500'
                    }`}
                  >
                    Use default contact
                  </button>
                  <button
                    onClick={() => setUseDefaultPhone(false)}
                    className={`w-full p-2 rounded-md border transition-colors ${
                      !useDefaultPhone
                        ? 'bg-red-500 text-white border-red-500'
                        : 'bg-gray-800 text-white border-gray-600 hover:border-red-500'
                    }`}
                  >
                    Use different contact
                  </button>
                </div>
                {!useDefaultPhone && (
                  <input
                    type="tel"
                    value={differentPhone}
                    onChange={(e) => setDifferentPhone(e.target.value)}
                    placeholder="Enter contact phone number"
                    className="w-full p-2 border border-gray-600 rounded-md bg-gray-800 text-white focus:ring-2 focus:ring-red-500"
                  />
                )}
              </div>

              {/* Payment Details Section */}
              <div className="mb-6 border-b border-gray-700 pb-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-red-500">
                  <User className="w-5 h-5 text-red-500" />
                  Payment Details
                </h3>
                {/* Toggle buttons for payer selection */}
                <div className="flex gap-4 mb-4">
                  <button
                    onClick={() => setIsDifferentPayer(false)}
                    className={`w-full p-2 rounded-md border transition-colors ${
                      !isDifferentPayer
                        ? 'bg-red-500 text-white border-red-500'
                        : 'bg-gray-800 text-white border-gray-600 hover:border-red-500'
                    }`}
                  >
                    I am the payer
                  </button>
                  <button
                    onClick={() => setIsDifferentPayer(true)}
                    className={`w-full p-2 rounded-md border transition-colors ${
                      isDifferentPayer
                        ? 'bg-red-500 text-white border-red-500'
                        : 'bg-gray-800 text-white border-gray-600 hover:border-red-500'
                    }`}
                  >
                    Different payer
                  </button>
                </div>
                {isDifferentPayer && (
                  <input
                    type="text"
                    value={payerName}
                    onChange={(e) => setPayerName(e.target.value)}
                    placeholder="Enter name for transfer"
                    className="w-full p-2 border border-gray-600 rounded-md bg-gray-800 text-white focus:ring-2 focus:ring-red-500"
                  />
                )}
              </div>

              {/* Delivery Address Section (only for delivery) */}
              {currentOrderDeliveryMethod === 'delivery' && (
                <div className="mt-6 border-t border-gray-700 pt-6">
                  <h4 className="text-md font-medium mb-4 flex items-center gap-2 text-red-500">
                    <MapPin className="w-5 h-5 text-red-500" />
                    Delivery Address
                  </h4>
                  {/* Toggle buttons for address type */}
                  <div className="flex gap-4 mb-4">
                    <button
                      onClick={() => setUseDefaultAddress(true)}
                      className={`w-full p-2 rounded-md border transition-colors ${
                        useDefaultAddress
                          ? 'bg-red-500 text-white border-red-500'
                          : 'bg-gray-800 text-white border-gray-600 hover:border-red-500'
                      }`}
                    >
                      Use default address
                    </button>
                    <button
                      onClick={() => setUseDefaultAddress(false)}
                      className={`w-full p-2 rounded-md border transition-colors ${
                        !useDefaultAddress
                          ? 'bg-red-500 text-white border-red-500'
                          : 'bg-gray-800 text-white border-gray-600 hover:border-red-500'
                      }`}
                    >
                      Use different address
                    </button>
                  </div>
                  {useDefaultAddress ? (
                    <div className="p-3 bg-gray-700 rounded-md">
                      <p className="text-gray-200">
                        {user?.address || 'No default address set. Please add one in your profile or choose a different address.'}
                      </p>
                      {user?.location && (
                        <p className="mt-2 text-sm text-gray-300">
                          Delivery Fee for {user.location}: ₦
                          {locationsData.find(loc => loc.name === user.location)?.fee.toLocaleString() || 0}
                        </p>
                      )}
                    </div>
                  ) : (
                    <>
                      <input
                        type="text"
                        value={differentAddress}
                        onChange={handleDifferentAddressChange}
                        placeholder="Enter delivery address"
                        className="w-full p-2 border border-gray-600 rounded-md bg-gray-800 text-white focus:ring-2 focus:ring-red-500"
                      />
                      {/* Dropdown for selecting location when using a different address */}
                      <div className="mt-2">
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                          Select Delivery Location
                        </label>
                        <select
                          value={selectedLocation}
                          onChange={handleLocationChange}
                          className="w-full p-2 border border-gray-600 rounded-md bg-gray-800 text-white focus:ring-2 focus:ring-red-500"
                        >
                          <option value="">Select Location</option>
                          {locationsData.map(loc => (
                            <option key={loc.id} value={loc.name}>
                              {loc.name} (₦{loc.fee})
                            </option>
                          ))}
                        </select>
                        {selectedLocation && (
                          <p className="mt-2 text-sm text-gray-300">
                            Delivery Fee: ₦
                            {locationsData.find(loc => loc.name === selectedLocation)?.fee.toLocaleString() || 0}
                          </p>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="mt-6 text-center">
          <button
            onClick={handlePayment}
            className="px-8 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 mx-auto"
          >
            {user ? 'Proceed to Payment' : 'Login to Checkout'}
          </button>
        </div>
      </div>

      <AuthDropdown
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialMode="login"
      />
    </div>
  );
};

export default Cart;
